import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Compass, Pencil, ArrowSquareOut } from "@phosphor-icons/react";
import { toast } from "sonner";
import API from "../lib/api";
import { Button } from "../components/ui/button";
import SetTrackerDialog from "../components/SetTrackerDialog";
import CurrentJobOverlay from "../components/CurrentJobOverlay";

/**
 * Per-operative full-screen vehicle tracker page.
 * Loads the operative's saved tracker URL, then renders it as an iframe that
 * fills almost the full viewport height. Admins can edit the URL inline.
 */
export default function OperativeTrackingPage() {
  const { user_id } = useParams();
  const [op, setOp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showEdit, setShowEdit] = useState(false);
  const [jobCtx, setJobCtx] = useState(null);

  const fetchTracker = async () => {
    setLoading(true);
    try {
      const { data } = await API.get(`/in-house/team/${user_id}/tracker`);
      setOp(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load tracker");
    } finally { setLoading(false); }
  };

  const fetchJobCtx = async () => {
    try {
      const { data } = await API.get(`/in-house/team/${user_id}/current-job`);
      setJobCtx(data);
    } catch {/* non-fatal */}
  };

  useEffect(() => { fetchTracker(); fetchJobCtx(); }, [user_id]);

  // Refresh the job context every 60s so the "Live" badge stays accurate
  useEffect(() => {
    const t = setInterval(fetchJobCtx, 60_000);
    return () => clearInterval(t);
  }, [user_id]);

  if (loading) {
    return <div className="text-center text-muted-foreground py-16">Loading tracker…</div>;
  }
  if (!op) return null;

  return (
    <div className="space-y-4 h-[calc(100vh-7rem)] flex flex-col" data-testid="operative-tracking-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <Link to="/in-house/tracking" className="text-xs text-blue-600 hover:underline flex items-center gap-1" data-testid="tracking-back-link">
            <ArrowLeft size={14} /> Back to all trackers
          </Link>
          <h1 className="text-2xl font-[Chivo] font-bold mt-1 flex items-center gap-2">
            <Compass className="h-6 w-6 text-blue-600" weight="duotone" /> {op.name}
          </h1>
          <p className="text-xs text-muted-foreground">{op.email}</p>
        </div>
        <div className="flex items-center gap-2">
          {op.tracker_embed_url && (
            <a
              href={op.tracker_embed_url}
              target="_blank"
              rel="noreferrer noopener"
              className="text-xs px-3 py-2 rounded-md border border-border hover:bg-muted transition-colors flex items-center gap-1.5"
              data-testid="tracking-open-tab-btn"
              title="Open the tracker provider in a new tab"
            >
              <ArrowSquareOut size={12} /> Open in new tab
            </a>
          )}
          <Button size="sm" variant="outline" onClick={() => setShowEdit(true)} data-testid="tracking-edit-btn">
            <Pencil size={12} className="mr-1" /> {op.tracker_embed_url ? "Edit URL" : "Set URL"}
          </Button>
        </div>
      </div>

      {op.tracker_embed_url ? (
        <div className="flex-1 border border-border rounded-lg overflow-hidden bg-muted/30 relative">
          <iframe
            key={op.tracker_embed_url}
            src={op.tracker_embed_url}
            title={`${op.name} live tracker`}
            className="absolute inset-0 w-full h-full"
            sandbox="allow-scripts allow-same-origin allow-popups allow-forms allow-downloads"
            referrerPolicy="no-referrer"
            loading="eager"
            data-testid="tracking-iframe"
          />
          <CurrentJobOverlay data={jobCtx} variant="floating" dtPrefix="tracking-job-overlay" />
        </div>
      ) : (
        <div className="flex-1 border-2 border-dashed border-border rounded-lg p-16 flex flex-col items-center justify-center text-center">
          <Compass className="h-12 w-12 text-muted-foreground" weight="duotone" />
          <p className="mt-4 font-semibold">No tracker URL configured for {op.name}</p>
          <p className="text-sm text-muted-foreground mt-1 max-w-md">
            Click <strong>Set URL</strong> above and paste the iframe snippet your tracker provider gave you (e.g. Quartix, Movolytics, Geotab).
          </p>
          <Button onClick={() => setShowEdit(true)} className="mt-4" data-testid="tracking-set-empty-btn">
            <Pencil size={14} className="mr-1" /> Paste iframe snippet
          </Button>
        </div>
      )}

      <SetTrackerDialog
        operative={showEdit ? op : null}
        onClose={() => setShowEdit(false)}
        onSaved={() => { setShowEdit(false); fetchTracker(); }}
      />
    </div>
  );
}
